'use client'

import { cn } from '@/lib/utils'

import {
  EASE,
  HAIRLINE,
  SectionHead,
  restoreDashes,
  useReveal,
} from '@/components/page-kit'
import { PAYROLL } from './data'

/* Each feature already carries a `stat` in the source data — "Zero rounding
   errors", "Auto-updated rules", "Delivered in seconds", "12-month trail".
   Carried out to the right edge on its own rule, the way a statement sets its
   amounts, so the section is built from content that already exists.

   Heading is a verbatim fragment of `description`. */

export function PayrollFeatures() {
  const list = useReveal<HTMLDivElement>(0.1)

  return (
    <section className="pt-16 sm:pt-20 md:pt-28">
      <div className="container mx-auto">
        <SectionHead
          eyebrow="Key features"
          title="Seamless, error-free payroll cycles."
        />

        <div ref={list.ref} className={cn('mt-10 border-t sm:mt-12', HAIRLINE)}>
          {PAYROLL.features.map((f, i) => (
            <article
              key={f.title}
              className={cn(
                'grid grid-cols-1 gap-x-10 gap-y-2 border-b py-7 sm:py-9',
                'lg:grid-cols-[1fr_minmax(0,240px)]',
                HAIRLINE
              )}
              style={{
                opacity: list.shown ? 1 : 0,
                transform: list.shown ? 'none' : 'translateY(12px)',
                transition: `opacity 520ms ${EASE} ${i * 90}ms, transform 520ms ${EASE} ${i * 90}ms`,
              }}
            >
              <div className="min-w-0">
                <div className="flex items-baseline gap-4">
                  <span className="w-6 shrink-0 text-[11px] tabular-nums text-muted-foreground/60">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <h3 className="font-heading text-lg font-normal leading-snug text-foreground sm:text-xl">
                    {f.title}
                  </h3>
                </div>
                <p className="mt-2 max-w-2xl pl-10 text-sm leading-relaxed text-muted-foreground sm:text-base">
                  {restoreDashes(f.description)}
                </p>
              </div>

              {/* The amount column. */}
              <p className="pl-10 font-heading text-lg font-normal leading-tight text-emerald-600 sm:text-xl lg:pl-0 lg:text-right dark:text-primary-active">
                {f.stat}
              </p>
            </article>
          ))}
        </div>
      </div>
    </section>
  )
}
